import { MinusIcon, PlusIcon } from '@phosphor-icons/react';
import { InputNumber } from 'antd';
import type { Control, FieldPath, FieldValues } from 'react-hook-form';

import { Button } from '@/components/common/button';

import { FormField } from '../form-field';

import styles from './available-count-field.module.scss';

export type AvailableCountFieldProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
> = {
  name: TName;
  control: Control<TFieldValues>;
  label?: string;
  required?: boolean;
};

/** Integer ≥ 0 with +/- controls; decrement disabled at 0 / empty. */
export function AvailableCountField<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
>({ name, control, label, required }: AvailableCountFieldProps<TFieldValues, TName>) {
  return (
    <FormField name={name} control={control} label={label} required={required}>
      {({ field, fieldState }) => {
        const current = typeof field.value === 'number' ? field.value : null;
        const atZero = current === null || current <= 0;
        return (
          <div className={styles.stepper}>
            <InputNumber
              id={name}
              className={styles.input}
              controls={false}
              min={0}
              step={1}
              precision={0}
              placeholder="Enter Count"
              status={fieldState.error ? 'error' : undefined}
              value={current}
              ref={field.ref}
              onBlur={field.onBlur}
              onChange={(v) => field.onChange(v ?? undefined)}
            />
            <Button
              htmlType="button"
              variant="outline"
              aria-label="Decrease available count"
              disabled={atZero}
              startIcon={<MinusIcon size={16} />}
              className={[styles.stepBtn, fieldState.error ? styles.stepBtnError : undefined]
                .filter(Boolean)
                .join(' ')}
              onClick={() => field.onChange(Math.max(0, (current ?? 0) - 1))}
            />
            <Button
              htmlType="button"
              variant="outline"
              aria-label="Increase available count"
              startIcon={<PlusIcon size={16} />}
              className={[styles.stepBtn, fieldState.error ? styles.stepBtnError : undefined]
                .filter(Boolean)
                .join(' ')}
              onClick={() => field.onChange((current ?? 0) + 1)}
            />
          </div>
        );
      }}
    </FormField>
  );
}
